<template>
  <div>
    <Header>Daily No-show Form</Header>
    <div class="tiled-bar-image"></div>
    <Main>
      <ValidationObserver v-slot="{ invalid, validate, errors, reset }">
        <form name="lateRouteForm" @submit.prevent="validate().then(() => onSubmit(invalid, reset))">
          <div class="grid grid-cols-3 gap-7 p-2">
            <ValidationProvider :rules="`required`" name="date" v-slot="{ errors }">
              <CalendarField
                class="pt-1.5"
                v-model="formValues.date"
                @input="updateDateSelections"
                :min="new Date(1900, 0, 1).toISOString().slice(0, 10)"
                :max="today()">
                No-Show Date
              </CalendarField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <ValidationProvider :rules="`required`" name="vehicle" v-slot="{ errors }">
              <VSelectionField
                class="pt-1.5"
                v-model="formValues.vehicle"
                @input="updateVehicleSelections"
                label="code"
                val="id"
                :options="vehicleList">
                Vehicle Number:
              </VSelectionField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <ValidationProvider :rules="`required`" name="school" v-slot="{ errors }">
              <VSelectionField
                class="pt-1.5"
                :options="schoolList"
                label="name"
                val="id"
                @input="updateSchoolSelections"
                v-model="formValues.school">
                School or Program Name
              </VSelectionField>
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
          </div>
          <div class="flex flex-col p-2">
            <div>
              No-Show Students:
            </div>
            <ValidationProvider :rules="`required`" name="students" v-slot="{ errors }">
              <v-select
                class="mt-2"
                name="students"
                :disabled="!formValues.date || !formValues.school"
                v-model="selectedStudents"
                :options="studentList"
                :getOptionLabel="student => student.first_name + ' ' + student.last_name"
                :reduce="student => ({ id: student.db_id, noShowValue: undefined, display: `${student.first_name} ${student.last_name}` })"
                :placeholder="
                  formValues.date && formValues.school
                    ? 'Please select no-show students here.'
                    : 'Please fill out the fields above before selecting students.'
                "
                multiple
                :select-on-key-codes="[188, 13, 9]"
              />
              <span
                class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                name="error-text">
                {{ errors[0] }}
              </span>
            </ValidationProvider>
            <div class="p-2 grid grid-cols-4 gap-2">
              <ValidationProvider
                v-for="(selectedStudent, index) in selectedStudents"
                :key="selectedStudent.id"
                :rules="`required`"
                name="studentDirection"
                v-slot="{ errors }">
                <RadioField
                  v-model="selectedStudents[index].noShowValue"
                  alignment="col"
                  :options="{
                    I: 'inbound (to school/facility)',
                    O: 'outbound (from school/facility)',
                    B: 'both',
                  }">
                  {{ selectedStudent.display }} didn't ride
                </RadioField>
                <span
                  class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                  name="error-text">
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </div>
          </div>
          <div class="flex flex-col justify-center mx-auto items-center">
            <div class="w-full rounded-md bg-gray-200 p-2 my-2"
              v-if="!canSubmit">
              <span class="uppercase text-gray-500">
                Missing Permissions to Submit Form
              </span>
            </div>
            <div class="flex flex-row">
              <span class="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1"
                v-if="displayFormHasErrors(errors)">
                There are errors in fields above.
              </span>
            </div>
            <SubmitButton
              class="rounded-md font-medium text-white bg-sky-600 px-3 py-2 hover:bg-blue-200 transition-colors shadow-sm"
              type="submit"
              color="blue"
              size="md"
              :disabled="!canSubmit"
              :submitting="submitting">
              Submit
            </SubmitButton>
          </div>
        </form>
      </ValidationObserver>
      <!-- <p>Endpoint Data: {{ formValues }}</p> -->
    </Main>
  </div>
</template>

<style scoped>
.tiled-bar-image {
  width: 100%;
  height: 25px;
  background-image: url("@/assets/Bar.png");
  background-repeat: repeat-x;
}
</style>

<script>
import Main from '../components/Main.vue'
import Header from '../components/Header.vue'

import SubmitButton from '../components/SubmitButton.vue'
import CalendarField from '../components/formComponents/CalendarField.vue'
import VSelectionField from '../components/formComponents/VSelectionField.vue'
import RadioField from '../components/formComponents/RadioField.vue'

import dailyNoShowFormService from '../services/pages/dailyNoShowForm.js'
import user from '../services/user/user.js'
import notifications from '../services/notifications.js'

import en from "vee-validate/dist/locale/en.json"
import { localize } from 'vee-validate'

function runLocalization() {
  localize({
    en: {
      messages: en.messages,
      names: {
        date: 'No-show Date',
        vehicle: 'Vehicle',
        school: 'School or Program',
        students: 'Student(s)',
        studentDirection: 'Student Direction',
      },
      fields: {
        date: {
          required: 'No Show Date is required.',
        },
        vehicle: {
          required: 'Vehicle is required.',
        },
        school: {
          required: 'School or Program is required.',
        },
        students: {
          required: 'Student(s) is/are required.',
        },
        studentDirection: {
          required: 'Student Inbound/Outbound selection is required.',
        },
      }
    }
  })
  localize('en')
}

export default {
  components: {
    Main,
    Header,
    SubmitButton,
    CalendarField,
    VSelectionField,
    RadioField,
  },
  data() {
    return {
      user: {},

      formValues: {},
      noShowDate: '',
      vehicleList: [],
      studentList: [],
      schoolList: [],

      selectedVehicle: '',
      selectedRoute: '',
      selectedStudents: [],
      selectedSchool: '',
      submitTime: '',

      canSubmit: true,
      submitting: false,
      submissionPromise: undefined,

      currApiPromise: undefined,
    }
  },
  methods: {
    today() {
      var today = new Date()
      var month = today.getMonth() + Number(1)
      if (month < 10) {
        month = '0' + month
      }
      var date = today.getDate()
      if (date < 10) {
        date = '0' + date
      }
      return today.getFullYear() + '-' + month + '-' + date
    },
    async onSubmit(formInvalid, reset) {
      if (formInvalid) {
        return false
      }
      this.submitting = true

      if (this.submissionPromise) {
        return false
      }

      let vehicleId = this.vehicleList.find(item => item.id === this.selectedVehicle).id
      const content = this.selectedStudents.map(student => {
        return {
          student: student.id,
          vehicle: vehicleId,
          event_type: student.noShowValue,
          event_date: this.getDate(),
        }
      })
      console.log(content)
      dailyNoShowFormService.postNewDailyNoShowForm(content)
        .then(() => {
          notifications.pushInfo(`Successfully submitted for vehicle: ${this.selectedVehicle}`)
          this.selectedStudents = []
          this.formValues = {
            date: this.noShowDate,
          }
        })
        .finally(() => {
          this.submitting = false
          this.submissionPromise = undefined
          reset()
          this.updateDateSelections()
        })
    },
    invalidMessage(fields) {
      const fieldNames = Object.keys(fields)
      const listOfNames = fieldNames.map(fieldName =>
        fieldName.replace('_', ' ')
      )
      return `Oops! There are some errors above. Invalid fields: ${listOfNames}`
    },
    getDate() {
      return this.noShowDate
    },
    updateDateSelections() {
      if (this.currApiPromise) return

      this.formValues.vehicle = this.selectedVehicle = undefined
      this.formValues.school = this.selectedSchool = undefined
      this.selectedStudents = []

      this.currApiPromise = dailyNoShowFormService.getNewDailyNoShowSequenceFromServer({
        date: this.getDate(),
      })
        .then(result => {
          this.vehicleList = result.data
        })
        .finally(() => {
          this.currApiPromise = undefined
        })
    },
    updateVehicleSelections() {
      this.noShowDate = this.formValues.date
      this.selectedVehicle = this.formValues.vehicle

      if (this.currApiPromise) return

      this.formValues.school = this.selectedSchool = undefined
      this.selectedStudents = []

      this.currApiPromise = dailyNoShowFormService.getNewDailyNoShowSequenceFromServer({
        date: this.getDate(),
        vehicleId: this.selectedVehicle,
      })
        .then(
          result => {
            this.schoolList = result.data
          },
          error => console.error(error)
        ).finally(() => {
          this.currApiPromise = undefined
        })
    },
    updateSchoolSelections() {
      this.noShowDate = this.formValues.date
      this.selectedVehicle = this.formValues.vehicle
      this.selectedSchool = this.formValues.school

      if (this.currApiPromise) return

      this.selectedStudents = []

      this.currApiPromise = dailyNoShowFormService.getNewDailyNoShowSequenceFromServer({
        date: this.getDate(),
        vehicleId: this.selectedVehicle,
        buildingId: this.selectedSchool,
      })
        .then(
          result => {
            this.studentList = result.data
          },
          error => console.error(error)
        ).finally(() => {
          this.currApiPromise = undefined
        })
    },
    displayFormHasErrors(errorsMap) {
      return Object.values(errorsMap).flat().length > 0
    },
    validateProvidedInfo() {
      this.canSubmit = dailyNoShowFormService.canSubmitForm(this.user)
    },
    checkUserCanViewPage() {
      if (!this.user.can('view_daily_no_show_form')) {
        this.$router.push({
          name: 'VendorForms'
        })
        return true
      }
    },
  },
  async created() {
    runLocalization()
    this.user = await user.get()
    if (this.checkUserCanViewPage()) {
      return
    }

    this.validateProvidedInfo()
  },
  async mounted() {
    this.noShowDate = this.today()
    this.formValues.date = this.noShowDate

    await user.ready()
    this.updateDateSelections()
  },
}
</script>
<style>
.no-ride-form div .formulate-input {
  margin-bottom: 7px;
}
.textarea .formulate-input div textarea {
  height: 300px;
  margin-top: 40px;
}
</style>
